import { Suspense, lazy, useMemo } from 'react';
import { useClient } from '../context/ClientContext';

const loadersCache = new Map();

const getComponentLoader = (clientId, templateCodigo, componentName) => {
  const cacheKey = `${clientId}-${templateCodigo}-${componentName}`;
  if (loadersCache.has(cacheKey)) return loadersCache.get(cacheKey);

  const loader = () =>
    // 1. Client-specific override
    import(`../clients/${clientId}/components/${componentName}`)
      .then(mod => ({ default: mod[componentName] || mod.default }))
      .catch(() =>
        // 2. Assigned template
        import(`../templates/${templateCodigo}/components/${componentName}`)
          .then(mod => ({ default: mod[componentName] || mod.default }))
          .catch(() =>
            // 3. Generic shared fallback
            import(`../shared/components/${componentName}`)
              .then(mod => ({ default: mod[componentName] || mod.default }))
          )
      );

  loadersCache.set(cacheKey, loader);
  return loader;
};

export const DynamicClientComponent = ({
  componentName,
  fallback = <div style={{ padding: 20, textAlign: 'center' }}>Cargando...</div>,
  ...props
}) => {
  const { clientId, templateCodigo } = useClient();

  const Component = useMemo(() => {
    if (!clientId || !componentName || !templateCodigo) return null;
    return lazy(getComponentLoader(clientId, templateCodigo, componentName));
  }, [clientId, templateCodigo, componentName]);

  if (!Component) return fallback;

  return (
    <Suspense fallback={fallback}>
      <Component {...props} />
    </Suspense>
  );
};
